import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Bot, Trash2, Loader2, ExternalLink, Settings2 } from "lucide-react";
import { PageHeader } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input, Label, Badge } from "../components/ui/input";
import { Dialog, DialogTrigger, DialogContent, DialogHeader } from "../components/ui/dialog";
import api, { formatApiErrorDetail } from "../lib/api";

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "" });
  const [slugTouched, setSlugTouched] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/projects", form);
      toast.success("Progetto creato");
      setOpen(false);
      setForm({ name: "", slug: "" });
      setSlugTouched(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo progetto e tutti i suoi dati?")) return;
    try {
      await api.delete(`/api/projects/${id}`);
      toast.success("Progetto eliminato");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader title="Progetti" subtitle="Agenti con avatar, voce, form e azioni verso API esterne">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-project-btn">
              <Plus className="h-4 w-4" /> Nuovo progetto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader title="Nuovo progetto" description="Un progetto è un agente pubblicabile: avatar, flusso e azioni." />
            <form onSubmit={create} className="space-y-4" data-testid="project-form">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : slugify(name) }));
                  }}
                  placeholder="Assistente reception"
                  data-testid="project-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Slug (URL pubblico)</Label>
                <Input
                  required
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
                  }}
                  placeholder="assistente-reception"
                  data-testid="project-slug-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="project-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea progetto"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="p-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : projects.length === 0 ? (
          <div className="grid-backdrop flex flex-col items-center border border-line bg-white/70 py-20 text-center">
            <Bot className="h-10 w-10 text-slate-300" />
            <p className="mt-4 font-display text-lg font-bold text-ink">Nessun progetto</p>
            <p className="mt-1 text-sm text-slate-500">Crea il tuo primo agente con avatar e azioni.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
            {projects.map((p) => (
              <div key={p.id} className="border border-line bg-white p-6" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center bg-brand-50 text-brand">
                    <Bot className="h-5 w-5" />
                  </div>
                  {p.is_active ? <Badge tone="success">Attivo</Badge> : <Badge>Disattivo</Badge>}
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-ink">{p.name}</h3>
                <p className="text-sm text-slate-500">/agent/{p.id}</p>
                <div className="mt-5 flex gap-2">
                  <Link to={`/dashboard/projects/${p.id}`} className="flex-1">
                    <Button size="sm" variant="outline" className="w-full" data-testid={`manage-project-${p.id}`}>
                      <Settings2 className="h-3.5 w-3.5" /> Gestisci
                    </Button>
                  </Link>
                  <a href={`/agent/${p.id}`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" data-testid={`open-project-${p.id}`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-project-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
